import React from "react";
import {
  Box,
  TextField,
  Typography,
  IconButton,
  Button,
  Paper,
  Divider,
  FormControlLabel,
  Switch,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { StepProps } from "../StepComponents";
import {
  POLL_MAX_OPTIONS,
  POLL_MIN_OPTIONS,
  PollOption,
} from "../../../domain/polls/types";
import { composerOutlinedFieldSx, composerPanelBorderSx } from "../composerBorderStyles";

function emptyOption(index: number): PollOption {
  return { optionId: `opt-${index}`, label: "", order: index };
}

const PollStep: React.FC<StepProps> = ({
  formData,
  errors,
  handleChange,
  noticeFlowId,
}) => {
  const options: PollOption[] =
    formData.pollOptions?.length
      ? formData.pollOptions
      : [emptyOption(0), emptyOption(1)];
  const claimMode = formData.pollMode === "claim";
  const isClaimsFlow = noticeFlowId === "claims";

  const setOptions = (next: PollOption[]) => {
    handleChange({
      target: {
        name: "pollOptions",
        value: next.map((o, i) => ({
          ...o,
          order: i,
          optionId: o.optionId || `opt-${i}`,
        })),
      },
    });
  };

  const updateOptionLabel = (index: number, label: string) => {
    const next = [...options];
    next[index] = { ...next[index], label };
    setOptions(next);
  };

  const addOption = () => {
    if (options.length >= POLL_MAX_OPTIONS) return;
    setOptions([...options, emptyOption(options.length)]);
  };

  const removeOption = (index: number) => {
    if (options.length <= POLL_MIN_OPTIONS) return;
    setOptions(options.filter((_, i) => i !== index));
  };

  const moveOption = (from: number, to: number) => {
    if (to < 0 || to >= options.length) return;
    const next = [...options];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    setOptions(next);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Box>
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", fontWeight: 600, letterSpacing: 0.5 }}
        >
          {isClaimsFlow ? "TITLE" : "QUESTION"}
        </Typography>
        <TextField
          fullWidth
          name="title"
          value={formData.title}
          onChange={handleChange}
          placeholder={
            isClaimsFlow
              ? "What can parents claim?"
              : "What would you like to ask?"
          }
          error={!!errors.title}
          helperText={errors.title}
          sx={[composerOutlinedFieldSx, { mt: 0.5 }]}
        />
      </Box>

      <Box>
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", fontWeight: 600, letterSpacing: 0.5 }}
        >
          OPTIONS
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 0.25, mb: 0.75 }}
        >
          Add between {POLL_MIN_OPTIONS} and {POLL_MAX_OPTIONS} choices.
        </Typography>
        {errors.pollOptions && (
          <Typography variant="body2" color="error" sx={{ mb: 0.75 }}>
            {errors.pollOptions}
          </Typography>
        )}
        <Paper sx={{ ...composerPanelBorderSx, overflow: "hidden" }}>
          {options.map((opt, index) => (
            <Box key={opt.optionId || index}>
              {index > 0 && <Divider />}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 1.5,
                  py: 0.75,
                }}
              >
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <IconButton
                    size="small"
                    aria-label="Move up"
                    disabled={index === 0}
                    onClick={() => moveOption(index, index - 1)}
                  >
                    <DragIndicatorIcon fontSize="small" sx={{ opacity: 0.5 }} />
                  </IconButton>
                </Box>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={index === options.length - 1 && !opt.label ? "Add option" : "Option"}
                  value={opt.label}
                  onChange={(e) => updateOptionLabel(index, e.target.value)}
                  variant="standard"
                  InputProps={{ disableUnderline: true }}
                />
                <IconButton
                  size="small"
                  aria-label="Remove option"
                  disabled={options.length <= POLL_MIN_OPTIONS}
                  onClick={() => removeOption(index)}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          ))}
        </Paper>
        {options.length < POLL_MAX_OPTIONS && (
          <Button
            startIcon={<AddIcon />}
            onClick={addOption}
            sx={{ mt: 0.75 }}
            size="small"
          >
            Add option
          </Button>
        )}
      </Box>

      {!isClaimsFlow && (
        <Box sx={{ mt: 0.25 }}>
          <FormControlLabel
            sx={{
              alignItems: "flex-start",
              ml: 0,
              mr: 0,
              gap: 0.5,
              "& .MuiSwitch-root": { mt: 0.25 },
            }}
            control={
              <Switch
                size="small"
                checked={claimMode}
                onChange={(event) =>
                  handleChange({
                    target: {
                      name: "pollMode",
                      value: event.target.checked ? "claim" : "standard",
                    },
                  })
                }
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.35 }}>
                  One claim per option (options disappear after selection)
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 0.25, lineHeight: 1.35 }}
                >
                  Use for sign-up lists where each choice can only be taken once.
                </Typography>
              </Box>
            }
          />
        </Box>
      )}
    </Box>
  );
};

export default PollStep;
